/**
 * SloSettings — org-level SLO target editor.
 *
 * Canonical layout: page header with `text-display` title, Card-wrapped
 * sections. No breadcrumbs.
 */

import React, { useEffect, useState } from 'react';
import { Save, Trash2 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/contexts/AuthContext';
import { Card, Button, Spinner, EmptyState, DeleteConfirmDialog } from '@/components/ui';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/Alert';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useToast } from '@/contexts/ToastContext';
import { deleteSloOverride, fetchSloSettings, updateSloConfig, type SloSettingsResponse } from '@/services/reportsService';
import type { SloConfig, SloOverride } from '@simcheck/shared-types';

export default function SloSettings() {
  const { activeOrganization } = useAuth();
  const toast = useToast();
  const [data, setData] = useState<SloSettingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Override waiting for confirmation in the DeleteConfirmDialog
  const [pendingOverride, setPendingOverride] = useState<SloOverride | null>(null);
  const [draft, setDraft] = useState<Partial<SloConfig>>({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!activeOrganization?.id) return;
      setLoading(true);
      setError(null);
      try {
        const resp = await fetchSloSettings(activeOrganization.id);
        if (!cancelled) {
          setData(resp);
          setDraft(resp.config);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load SLO settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [activeOrganization?.id]);

  async function save() {
    if (!activeOrganization?.id) return;
    setSaving(true);
    try {
      await updateSloConfig(activeOrganization.id, draft);
      toast.success('SLO targets saved');
      const resp = await fetchSloSettings(activeOrganization.id);
      setData(resp);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function removeOverride(id: string) {
    if (!activeOrganization?.id) return;
    try {
      await deleteSloOverride(activeOrganization.id, id);
      toast.success('Override removed');
      const resp = await fetchSloSettings(activeOrganization.id);
      setData(resp);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2">SLO targets</h1>
            <p className="text-body text-muted-foreground">
              Set org-level pass-rate targets. Reports color cells against these — burn rate ≥ 2× the target turns red.
            </p>
          </div>
        </div>

        {error ? (
          <Alert variant="destructive">
            <AlertTitle>Failed to load</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : data ? (
          <>
            <Card padding="lg">
              <div className="text-caption text-muted-foreground uppercase mb-4">Org defaults</div>
              <div className="grid gap-4 sm:grid-cols-2">
                <SloField
                  label="Connectivity attach rate"
                  suffix="(0–1)"
                  value={draft.connectivityAttach ?? data.config.connectivityAttach}
                  onChange={(v) => setDraft({ ...draft, connectivityAttach: v })}
                />
                <SloField
                  label="Data test success rate"
                  suffix="(0–1)"
                  value={draft.dataSuccess ?? data.config.dataSuccess}
                  onChange={(v) => setDraft({ ...draft, dataSuccess: v })}
                />
                <SloField
                  label="Voice MOS floor"
                  suffix="(MOS)"
                  value={draft.voiceMosFloor ?? data.config.voiceMosFloor}
                  onChange={(v) => setDraft({ ...draft, voiceMosFloor: v })}
                />
                <SloField
                  label="SMS delivery rate"
                  suffix="(0–1)"
                  value={draft.smsDeliveryRate ?? data.config.smsDeliveryRate}
                  onChange={(v) => setDraft({ ...draft, smsDeliveryRate: v })}
                />
              </div>
              <div className="mt-4">
                <Button onClick={save} disabled={saving} icon={<Save className="w-4 h-4" />}>
                  Save targets
                </Button>
              </div>
            </Card>

            <Card padding="lg">
              <div className="text-caption text-muted-foreground uppercase mb-4">
                Per-cell overrides ({data.overrides.length})
              </div>
              {data.overrides.length === 0 ? (
                <EmptyState
                  title="No overrides"
                  description="Create per-cell overrides from the reports UI (coming soon) or via API."
                />
              ) : (
                <div className="flex flex-col gap-1">
                  {data.overrides.map((o) => (
                    <div
                      key={o.id}
                      className="flex items-center justify-between rounded border border-border px-3 py-2 text-sm"
                    >
                      <div>
                        <span className="font-medium">{o.metric}</span>
                        <span className="text-muted-foreground"> · {o.scope}:{o.scopeId}</span>
                        <span className="ml-2 tabular-nums">{o.threshold}</span>
                        {o.note ? <span className="ml-2 italic text-muted-foreground">— {o.note}</span> : null}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPendingOverride(o)}
                        icon={<Trash2 className="w-4 h-4" />}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </>
        ) : null}
      </div>

      <DeleteConfirmDialog
        open={pendingOverride !== null}
        onClose={() => setPendingOverride(null)}
        title="Remove override?"
        verb="remove"
        confirmText="Remove"
        itemName={pendingOverride ? `${pendingOverride.metric} · ${pendingOverride.scope}:${pendingOverride.scopeId}` : undefined}
        details="This cell goes back to the organization's SLO targets."
        onConfirm={async () => {
          if (pendingOverride) await removeOverride(pendingOverride.id);
        }}
      />
    </AppShell>
  );
}

function SloField({
  label, suffix, value, onChange,
}: { label: string; suffix: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs">
        {label} <span className="text-muted-foreground">{suffix}</span>
      </Label>
      <Input
        type="number"
        step="0.01"
        min={0}
        max={5}
        value={value}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (!Number.isNaN(v)) onChange(v);
        }}
      />
    </div>
  );
}
